/**
 * Swimmable API Client
 * Official JavaScript/TypeScript SDK for the Swimmable API
 */

import fetch from 'cross-fetch';
import {
  SwimmableConfig,
  Coordinates,
  BasicConditions,
  EnhancedConditions,
  SpotsResponse,
  ApiError,
  RequestOptions,
  ApiResponse,
  HealthStatus,
  UsageStats,
  ApiKeyInfo,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
} from './types';

export class SwimmableClient {
  private config: Required<SwimmableConfig>;

  constructor(config: SwimmableConfig = {}) {
    this.config = {
      apiKey: config.apiKey || '',
      baseUrl: config.baseUrl || 'https://api.swimmable.app',
      timeout: config.timeout || 10000,
      headers: config.headers || {},
    };
  }

  /**
   * Make an HTTP request to the Swimmable API
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions & {
      method?: string;
      body?: any;
      requiresAuth?: boolean;
    } = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      body,
      timeout = this.config.timeout,
      headers = {},
      requiresAuth = false,
    } = options;

    const url = `${this.config.baseUrl}${endpoint}`;
    
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'User-Agent': 'Swimmable-JS-SDK/1.0.0',
      ...this.config.headers,
      ...headers,
    };

    // Add authentication header if API key is provided or required
    if (this.config.apiKey) {
      requestHeaders['X-API-Key'] = this.config.apiKey;
    } else if (requiresAuth) {
      throw new Error('API key is required for this endpoint. Please provide an apiKey in the configuration.');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      let data: T;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = (await response.text()) as unknown as T;
      }

      if (!response.ok) {
        const error = data as unknown as ApiError;
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return {
        data,
        status: response.status,
        headers: responseHeaders,
      };
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
      
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * Get basic swimming conditions for a location
   * @param coordinates - Latitude and longitude
   * @param options - Request options
   */
  async getConditions(
    coordinates: Coordinates,
    options?: RequestOptions
  ): Promise<BasicConditions> {
    const endpoint = `/api/public/conditions?lat=${coordinates.lat}&lon=${coordinates.lon}`;
    const response = await this.request<BasicConditions>(endpoint, options);
    return response.data;
  }

  /**
   * Get enhanced swimming conditions with detailed analysis
   * @param coordinates - Latitude and longitude
   * @param options - Request options
   */
  async getEnhancedConditions(
    coordinates: Coordinates,
    options?: RequestOptions
  ): Promise<EnhancedConditions> {
    const endpoint = `/api/public/conditions/enhanced?lat=${coordinates.lat}&lon=${coordinates.lon}`;
    const response = await this.request<EnhancedConditions>(endpoint, options);
    return response.data;
  }

  /**
   * Get list of available swimming spots
   * @param options - Request options
   */
  async getSpots(options?: RequestOptions): Promise<SpotsResponse> {
    const endpoint = '/api/public/spots';
    const response = await this.request<SpotsResponse>(endpoint, options);
    return response.data;
  }

  /**
   * Get API health status
   * @param options - Request options
   */
  async getHealth(options?: RequestOptions): Promise<HealthStatus> {
    const endpoint = '/api/health';
    const response = await this.request<HealthStatus>(endpoint, options);
    return response.data;
  }

  /**
   * Get your API usage statistics (requires API key)
   * @param days - Number of days to look back (default: 30)
   * @param options - Request options
   */
  async getUsageStats(days: number = 30, options?: RequestOptions): Promise<UsageStats> {
    const endpoint = `/api/keys/usage?days=${days}`;
    const response = await this.request<UsageStats>(endpoint, {
      ...options,
      requiresAuth: true,
    });
    return response.data;
  }

  /**
   * Get your API keys (requires authentication)
   * @param options - Request options
   */
  async getApiKeys(options?: RequestOptions): Promise<ApiKeyInfo[]> {
    const endpoint = '/api/keys';
    const response = await this.request<{ api_keys: ApiKeyInfo[] }>(endpoint, {
      ...options,
      requiresAuth: true,
    });
    return response.data.api_keys;
  }

  /**
   * Create a new API key (requires authentication)
   * @param keyData - API key creation data
   * @param options - Request options
   */
  async createApiKey(
    keyData: CreateApiKeyRequest,
    options?: RequestOptions
  ): Promise<CreateApiKeyResponse> {
    const endpoint = '/api/keys';
    const response = await this.request<CreateApiKeyResponse>(endpoint, {
      ...options,
      method: 'POST',
      body: keyData,
      requiresAuth: true,
    });
    return response.data;
  }

  /**
   * Revoke an API key (requires authentication)
   * @param keyId - ID of the API key to revoke
   * @param options - Request options
   */
  async revokeApiKey(keyId: string, options?: RequestOptions): Promise<{ success: boolean }> {
    const endpoint = `/api/keys/${keyId}`;
    const response = await this.request<{ success: boolean }>(endpoint, {
      ...options,
      method: 'DELETE',
      requiresAuth: true,
    });
    return response.data;
  }

  /**
   * Update the API key for this client
   * @param apiKey - New API key
   */
  setApiKey(apiKey: string): void {
    this.config.apiKey = apiKey;
  }

  /**
   * Get the current configuration
   */
  getConfig(): SwimmableConfig {
    return { ...this.config };
  }

  /**
   * Update client configuration
   * @param config - Configuration updates
   */
  updateConfig(config: Partial<SwimmableConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

/**
 * Create a new Swimmable client instance
 * @param config - Client configuration
 */
export function createClient(config?: SwimmableConfig): SwimmableClient {
  return new SwimmableClient(config);
}

/**
 * Default client instance (can be configured globally)
 */
export const swimmable = new SwimmableClient();